import { useEffect, useRef } from "react";

import { refreshSessionIfNeeded } from "@/lib/sessionRefresh";

const DEFAULT_CHECK_INTERVAL_MS = 60 * 1000;
const ACTIVITY_REFRESH_THROTTLE_MS = 60 * 1000;
const ACTIVE_EVENTS = [
  "keydown",
  "mousedown",
  "mousemove",
  "scroll",
  "touchstart",
] as const;

export function useSessionKeepAlive({
  enabled,
  refreshOnMount = false,
  intervalMs = DEFAULT_CHECK_INTERVAL_MS,
}: {
  enabled: boolean;
  refreshOnMount?: boolean;
  intervalMs?: number;
}): void {
  const lastActivityRefreshRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;

    const refreshIfVisible = (force = false) => {
      if (document.visibilityState === "hidden") return;
      void refreshSessionIfNeeded({ force });
    };

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastActivityRefreshRef.current < ACTIVITY_REFRESH_THROTTLE_MS) {
        return;
      }
      lastActivityRefreshRef.current = now;
      refreshIfVisible();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshIfVisible();
      }
    };

    if (refreshOnMount) {
      refreshIfVisible();
    }

    const intervalId = window.setInterval(() => {
      refreshIfVisible();
    }, intervalMs);

    ACTIVE_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, handleActivity, { passive: true });
    });
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      ACTIVE_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, handleActivity);
      });
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [enabled, intervalMs, refreshOnMount]);
}
